/** Browser-local watchlist service. */

export interface WatchlistItem {
  ticker: string;
  added_at: string;
}

export interface WatchlistResponse {
  watchlist: WatchlistItem[];
}

export interface WatchlistOperationResponse {
  success: boolean;
  message: string;
}

const WATCHLIST_STORAGE_KEY = 'tkrscan.watchlist';
const MAX_WATCHLIST_ITEMS = 200;
let inMemoryWatchlist: WatchlistItem[] = [];

function normalizeTicker(ticker: string): string {
  return (ticker || '')
    .trim()
    .replace(/[^a-zA-Z0-9.]/g, '')
    .toUpperCase();
}

function readRawStorage(): string | null {
  try {
    return window.localStorage.getItem(WATCHLIST_STORAGE_KEY);
  } catch {
    return JSON.stringify(inMemoryWatchlist);
  }
}

function writeRawStorage(items: WatchlistItem[]): void {
  inMemoryWatchlist = items;
  try {
    window.localStorage.setItem(WATCHLIST_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // localStorage may be unavailable (privacy mode/blocked storage); fallback is in-memory.
  }
}

function parseWatchlistStorage(): WatchlistItem[] {
  try {
    const raw = readRawStorage();
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(item => item && typeof item.ticker === 'string' && typeof item.added_at === 'string')
      .map(item => ({
        ticker: normalizeTicker(item.ticker),
        added_at: item.added_at,
      }))
      .filter(item => item.ticker.length > 0)
      .sort((a, b) => new Date(b.added_at).getTime() - new Date(a.added_at).getTime());
  } catch {
    return [];
  }
}

function saveWatchlistStorage(items: WatchlistItem[]): void {
  writeRawStorage(items);
}

/**
 * Fetch user's watchlist.
 */
export async function fetchWatchlist(): Promise<WatchlistResponse | null> {
  try {
    return { watchlist: parseWatchlistStorage() };
  } catch (error) {
    console.error('Error fetching watchlist:', error);
    throw error;
  }
}

/**
 * Add a ticker to the watchlist.
 */
export async function addToWatchlist(ticker: string): Promise<WatchlistOperationResponse> {
  try {
    const normalizedTicker = normalizeTicker(ticker);
    if (!normalizedTicker) {
      return { success: false, message: 'Ticker is required' };
    }

    const watchlist = parseWatchlistStorage();
    if (watchlist.some(item => item.ticker === normalizedTicker)) {
      return { success: false, message: `${normalizedTicker} is already in your watchlist` };
    }

    if (watchlist.length >= MAX_WATCHLIST_ITEMS) {
      return { success: false, message: `Watchlist is full (max ${MAX_WATCHLIST_ITEMS} tickers)` };
    }

    const next = [{ ticker: normalizedTicker, added_at: new Date().toISOString() }, ...watchlist];
    saveWatchlistStorage(next);
    return { success: true, message: `Added ${normalizedTicker} to watchlist` };
  } catch (error) {
    console.error(`Error adding ${ticker} to watchlist:`, error);
    throw error;
  }
}

/**
 * Remove a ticker from the watchlist.
 */
export async function removeFromWatchlist(ticker: string): Promise<WatchlistOperationResponse> {
  try {
    const normalizedTicker = normalizeTicker(ticker);
    if (!normalizedTicker) {
      return { success: false, message: 'Ticker is required' };
    }

    const watchlist = parseWatchlistStorage();
    const next = watchlist.filter(item => item.ticker !== normalizedTicker);
    if (next.length === watchlist.length) {
      return { success: false, message: `${normalizedTicker} was not found in your watchlist` };
    }

    saveWatchlistStorage(next);
    return { success: true, message: `Removed ${normalizedTicker} from watchlist` };
  } catch (error) {
    console.error(`Error removing ${ticker} from watchlist:`, error);
    throw error;
  }
}

